import React, { useEffect, useState } from 'react';
import { SupportRef } from '../../support/SupportRef';
import { traceUrl } from '../agent-common/TraceLink';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { getPromises, type PromiseRow, type PromiseQueue } from './api';

/** Polling follows a delivery even when the asker left the original room. */
export default function Promises({ agentId, threadId, excludeIds = [] }: {
  agentId?: string; threadId?: string; excludeIds?: string[];
}) {
  const generation = useVoyagerStore((s) => s.agentsGeneration);
  const navigate = useVoyagerStore((s) => s.navigateToAgentWall);
  const [result, setResult] = useState<{ generation: number; key: string; queue: PromiseQueue } | null>(null);
  const [error, setError] = useState(false);
  const key = `${agentId || 'home'}:${threadId || ''}`;
  useEffect(() => {
    let active = true;
    let disabled = false;
    let running = false;
    setResult(null);
    setError(false);
    const refresh = async () => {
      if (!active || disabled || running) return;
      running = true;
      try {
        const queue = await getPromises(agentId);
        if (active) { setResult({ generation, key, queue }); setError(false); }
      } catch (e: any) {
        if (active) {
          disabled = e?.response?.status === 404;
          setResult(null);
          setError(!disabled);
        }
      } finally { running = false; }
    };
    refresh();
    const interval = window.setInterval(refresh, 10000);
    return () => { active = false; window.clearInterval(interval); };
  }, [agentId, generation, key]);

  const queue = result?.generation === generation && result?.key === key ? result.queue : null;
  if (!queue) return error ? <Typography role="status" variant="caption">Promises could not be loaded. Retrying…</Typography> : null;
  const groups: [string, PromiseRow[]][] = agentId
    ? [[threadId ? 'Follow-ups' : 'Promises', queue.to_me.filter((p) => !threadId || p.thread_id === threadId)]]
    : [['Promises to you', queue.to_me], ['Promises by your agents', queue.by_my_agents]];
  return <Box data-testid="promises" sx={{ width: '100%', display: 'grid', gap: 1.5, my: 2 }}>
    {groups.map(([title, rows]) => <Box key={title}>
      <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>{title}</Typography>
      {!rows.length && <Typography variant="caption" color="text.secondary">No promises yet.</Typography>}
      {rows.filter((p) => !excludeIds.includes(`promise_${p.id}`)).map((p) => <Box key={p.id} data-testid="promise-row"
        sx={{ border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', borderRadius: 2, p: 1.25, mb: 0.75 }}>
        <Typography sx={{ fontSize: 13 }}>{p.question}</Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Asked by {p.asked_by_name || (p.room === 'meeting' ? 'a meeting participant' : p.asked_by)}
          {p.room === 'meeting' && ` (in ${p.meeting_title || 'a meeting'})`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {p.status === 'open' ? `Checking · due ${p.due_at ? new Date(p.due_at).toLocaleString() : 'soon'}` : p.status}
        </Typography>
        {(p.text || p.outcome) && <Typography sx={{ fontSize: 13, mt: 0.5, whiteSpace: 'pre-wrap' }}>{p.text || p.outcome}</Typography>}
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          {!threadId && <Link component="button" onClick={() => navigate(p.agent_id)} sx={{ fontSize: 12 }}>Open wall</Link>}
          <SupportRef session={p.session_id} />
          {p.session_id && <Link href={traceUrl({ session: p.session_id })} target="_blank" rel="noopener" sx={{ fontSize: 12 }}>Trace</Link>}
        </Box>
      </Box>)}
    </Box>)}
  </Box>;
}
